import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './Redux/store';
import { setAuth } from './Redux/authSlice';
import { FaTachometerAlt, FaUserGraduate, FaChalkboard, FaChalkboardTeacher, FaUserFriends, FaMoneyBillWave, FaWallet, FaCalendarAlt, FaGraduationCap, FaUserShield, FaChartBar, FaUsers, FaUserCircle, FaSignOutAlt, FaBullhorn } from 'react-icons/fa';
import InstallPWA from './InstallPWA';

const Sidebar: React.FC = () => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    students: false,
    classes: false,
    teachers: false,
    parents: false,
    fees: false,
    finances: false,
    exams: false,
    reports: false,
    users: false
  });

  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth > 980);
  const location = useLocation();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/users/user');
        setUserRole(response.data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
      }
    };
    fetchUserData();

    const handleResize = () => {
      if (window.innerWidth > 980) {
        setIsSidebarVisible(true);
      } else {
        setIsSidebarVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  const toggleSidebarVisibility = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const toggleMenu = (menu: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const getCookie = (name: string) => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  const handleLogout = () => {
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    dispatch(setAuth(false));
    window.location.href = '/';
  };

  if (!auth) return null;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={toggleSidebarVisibility}
        className="btn position-fixed d-lg-none rounded-circle shadow-lg d-flex align-items-center justify-content-center border-0"
        style={{
          top: '20px',
          left: '20px',
          width: '50px',
          height: '50px',
          background: '#3b82f6',
          color: '#fff',
          zIndex: 1050
        }}
      >
        <i className={`fas ${isSidebarVisible ? 'fa-times' : 'fa-bars'} fs-5`}></i>
      </button>

      {/* Sidebar Overlay for Mobile */}
      {isSidebarVisible && (
        <div
          className="d-lg-none position-fixed top-0 start-0 w-100 vh-100 bg-dark bg-opacity-50"
          style={{ zIndex: 1040, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={toggleSidebarVisibility}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`vh-100 position-fixed d-flex flex-column sidebar ${isSidebarVisible ? 'sidebar-show' : 'sidebar-hide'}`}
        style={{
          width: '250px',
          background: '#0f172a', // Slate 900
          color: '#e2e8f0', // Slate 200
          boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          fontFamily: "'Inter', sans-serif",
          zIndex: 1045,
          borderRight: '1px solid #1e293b'
        }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-bottom border-secondary border-opacity-25 d-flex flex-column align-items-center">
          <div className="mb-3 d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
            <img src="/sanabiland-logo.jpg" alt="Sanabiland School" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
          </div>
          <h4 className="fw-bold mb-1 text-white breaking-word text-center" style={{ letterSpacing: '-0.5px' }}>
            Sanabil Schools
          </h4>
        </div>

        {/* Sidebar Navigation */}
        <div className="flex-grow-1 overflow-auto custom-scrollbar p-3">
          <nav>
            <ul className="nav flex-column gap-1">
              <li className="nav-item mb-2">
                <Link to="/dashboard" className={`nav-link d-flex align-items-center py-2 px-3 rounded-3 ${location.pathname === '/dashboard' ? 'active-link' : 'text-slate-300 hover-bg-slate'}`}>
                  <FaTachometerAlt className="width-20 me-3" />
                  <span className="fw-medium">Muuraayad</span>
                </Link>
              </li>

              {/* Students Module */}
              <li className="nav-item">
                <div
                  className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.students ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                  onClick={() => toggleMenu('students')}
                >
                  <div className="d-flex align-items-center">
                    <FaUserGraduate className="width-20 me-3" />
                    <span className="fw-medium">Ardayda</span>
                  </div>
                  <i className={`fas fa-chevron-${expandedMenus.students ? 'up' : 'down'} small opacity-50`}></i>
                </div>
                {expandedMenus.students && (
                  <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                    <li className="nav-item"><Link to="/students" className={`nav-link py-1 ps-3 small ${location.pathname === '/students' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Ardayda Oo Dhan</Link></li>
                    {/* Teachers cannot add students */}
                    {userRole !== 'teacher' && (
                      <>
                        <li className="nav-item"><Link to="/add-students" className={`nav-link py-1 ps-3 small ${location.pathname === '/add-students' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Diiwaangellin</Link></li>
                        <li className="nav-item"><Link to="/classOfStudent" className={`nav-link py-1 ps-3 small ${location.pathname === '/classOfStudent' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Fasalka</Link></li>
                      </>
                    )}
                  </ul>
                )}
              </li>

              {/* Classes Module */}
              <li className="nav-item">
                <div
                  className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.classes ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                  onClick={() => toggleMenu('classes')}
                >
                  <div className="d-flex align-items-center">
                    <FaChalkboard className="width-20 me-3" />
                    <span className="fw-medium">Fasallada</span>
                  </div>
                  <i className={`fas fa-chevron-${expandedMenus.classes ? 'up' : 'down'} small opacity-50`}></i>
                </div>
                {expandedMenus.classes && (
                  <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                    <li className="nav-item"><Link to="/classes" className={`nav-link py-1 ps-3 small ${location.pathname === '/classes' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Fasallada Oo Dhan</Link></li>
                    <li className="nav-item"><Link to="/section" className={`nav-link py-1 ps-3 small ${location.pathname === '/section' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Qeybaha Fasalka</Link></li>
                  </ul>
                )}
              </li>

              {/* Teachers Module - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.teachers ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('teachers')}
                  >
                    <div className="d-flex align-items-center">
                      <FaChalkboardTeacher className="width-20 me-3" />
                      <span className="fw-medium">Macalimiinta</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.teachers ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.teachers && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/teachers" className={`nav-link py-1 ps-3 small ${location.pathname === '/teachers' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Macalimiinta Oo Dhan</Link></li>
                      <li className="nav-item"><Link to="/add-teachers" className={`nav-link py-1 ps-3 small ${location.pathname === '/add-teachers' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Ku Dar Macalin</Link></li>
                    </ul>
                  )}
                </li>
              )}

              {/* Parents Module - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.parents ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('parents')}
                  >
                    <div className="d-flex align-items-center">
                      <FaUserFriends className="width-20 me-3" />
                      <span className="fw-medium">Waalidiinta</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.parents ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.parents && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/parents" className={`nav-link py-1 ps-3 small ${location.pathname === '/parents' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Waalidiinta Oo Dhan</Link></li>
                      <li className="nav-item"><Link to="/add-parent" className={`nav-link py-1 ps-3 small ${location.pathname === '/add-parent' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Ku Dar Waalid</Link></li>
                    </ul>
                  )}
                </li>
              )}

              {/* Fees Module - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.fees ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('fees')}
                  >
                    <div className="d-flex align-items-center">
                      <FaMoneyBillWave className="width-20 me-3" />
                      <span className="fw-medium">Lacagaha</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.fees ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.fees && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/fees" className={`nav-link py-1 ps-3 small ${location.pathname === '/fees' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Diiwaanka Lacagaha</Link></li>
                      <li className="nav-item"><Link to="/fees-structure" className={`nav-link py-1 ps-3 small ${location.pathname === '/fees-structure' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Qaab Dhismeedka</Link></li>
                      <li className="nav-item"><Link to="/add-fees" className={`nav-link py-1 ps-3 small ${location.pathname === '/add-fees' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Ururi Lacag</Link></li>
                      <li className="nav-item"><Link to="/fees_not_paid" className={`nav-link py-1 ps-3 small ${location.pathname === '/fees_not_paid' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Haray</Link></li>
                      <li className="nav-item"><Link to="/fees-pending" className={`nav-link py-1 ps-3 small ${location.pathname === '/fees-pending' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Qeyb Bixiyay</Link></li>
                    </ul>
                  )}
                </li>
              )}

              {/* Finances Module - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.finances ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('finances')}
                  >
                    <div className="d-flex align-items-center">
                      <FaWallet className="width-20 me-3" />
                      <span className="fw-medium">Maaliyadda</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.finances ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.finances && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/ExpenseList" className={`nav-link py-1 ps-3 small ${location.pathname === '/ExpenseList' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Kharash</Link></li>
                      <li className="nav-item"><Link to="/expenses" className={`nav-link py-1 ps-3 small ${location.pathname === '/expenses' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Ku Dar Kharash</Link></li>
                      <li className="nav-item"><Link to="/salaries" className={`nav-link py-1 ps-3 small ${location.pathname === '/salaries' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Mushaaraadka</Link></li>
                      <li className="nav-item"><Link to="/add-salary" className={`nav-link py-1 ps-3 small ${location.pathname === '/add-salary' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Bixi Mushaar</Link></li>
                    </ul>
                  )}
                </li>
              )}

              {/* Attendance */}
              <li className="nav-item">
                <Link to="/attendance-table" className={`nav-link d-flex align-items-center py-2 px-3 rounded-3 ${location.pathname === '/attendance-table' ? 'active-link' : 'text-slate-300 hover-bg-slate'}`}>
                  <FaCalendarAlt className="width-20 me-3" />
                  <span className="fw-medium">Xaadirinta</span>
                </Link>
              </li>

              {/* Exams Module */}
              <li className="nav-item">
                <div
                  className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.exams ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                  onClick={() => toggleMenu('exams')}
                >
                  <div className="d-flex align-items-center">
                    <FaGraduationCap className="width-20 me-3" />
                    <span className="fw-medium">Imtixaanaadka</span>
                  </div>
                  <i className={`fas fa-chevron-${expandedMenus.exams ? 'up' : 'down'} small opacity-50`}></i>
                </div>
                {expandedMenus.exams && (
                  <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                    {userRole !== 'teacher' && (
                      <li className="nav-item"><Link to="/exams" className={`nav-link py-1 ps-3 small ${location.pathname === '/exams' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Jadwal</Link></li>
                    )}
                    <li className="nav-item"><Link to="/marks" className={`nav-link py-1 ps-3 small ${location.pathname === '/marks' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Natiijooyinka</Link></li>
                    <li className="nav-item"><Link to="/enter-marks" className={`nav-link py-1 ps-3 small ${location.pathname === '/enter-marks' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Geli Dhibcaha</Link></li>
                    {userRole !== 'teacher' && (
                      <li className="nav-item"><Link to="/subjects" className={`nav-link py-1 ps-3 small ${location.pathname === '/subjects' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Maadooyinka</Link></li>
                    )}
                    {userRole !== 'teacher' && (
                      <>
                        <li className="nav-item"><Link to="/gradings" className={`nav-link py-1 ps-3 small ${location.pathname === '/gradings' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Darajooyinka</Link></li>
                        <li className="nav-item border-top border-secondary border-opacity-25 mt-1 pt-1"><Link to="/exam-configuration" className={`nav-link py-1 ps-3 small ${location.pathname === '/exam-configuration' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Habeyn</Link></li>
                      </>
                    )}
                  </ul>
                )}
              </li>

              {/* Discipline - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.discipline ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('discipline')}
                  >
                    <div className="d-flex align-items-center">
                      <FaUserShield className="width-20 me-3" />
                      <span className="fw-medium">Anshaxa</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.discipline ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.discipline && (
                    <ul className="nav flex-column ms-4 mt-1">
                      <li className="nav-item">
                        <Link to="/discipline" className={`nav-link d-flex align-items-center py-2 px-3 rounded-3 ${location.pathname === '/discipline' ? 'active-link' : 'text-slate-300 hover-bg-slate'}`}>
                          <span className="fw-medium small">Eeg Kiisaska</span>
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              )}

              {/* Reports - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.reports ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('reports')}
                  >
                    <div className="d-flex align-items-center">
                      <FaChartBar className="width-20 me-3" />
                      <span className="fw-medium">Warbixinada</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.reports ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.reports && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/reports" className={`nav-link py-1 ps-3 small ${location.pathname === '/reports' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>📊 Xudunta Warbixinada</Link></li>
                      <li className="nav-item"><Link to="/student-report" className={`nav-link py-1 ps-3 small ${location.pathname === '/student-report' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Warbixinta Ardayda</Link></li>
                      <li className="nav-item"><Link to="/attendance-report" className={`nav-link py-1 ps-3 small ${location.pathname === '/attendance-report' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Warbixinta Xaadirinta</Link></li>
                      <li className="nav-item"><Link to="/fees-report" className={`nav-link py-1 ps-3 small ${location.pathname === '/fees-report' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Warbixinta Lacagaha</Link></li>
                      <li className="nav-item"><Link to="/admission-report" className={`nav-link py-1 ps-3 small ${location.pathname === '/admission-report' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Warbixinta Diiwaangellinta</Link></li>
                      <li className="nav-item"><Link to="/income-report" className={`nav-link py-1 ps-3 small ${location.pathname === '/income-report' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Dakhliga & Kharashaadka</Link></li>
                    </ul>
                  )}
                </li>
              )}

              {/* Blog Posts - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item">
                  <Link to="/post" className={`nav-link d-flex align-items-center py-2 px-3 rounded-3 ${location.pathname === '/post' ? 'active-link' : 'text-slate-300 hover-bg-slate'}`}>
                    <FaBullhorn className="width-20 me-3" />
                    <span className="fw-medium">Maamulka Ogeysiisyada</span>
                  </Link>
                </li>
              )}

              {/* User Management - Hidden for teachers */}
              {userRole !== 'teacher' && (
                <li className="nav-item mt-2 pt-2 border-top border-secondary border-opacity-25">
                  <div
                    className={`nav-link d-flex align-items-center justify-content-between py-2 px-3 rounded-3 cursor-pointer ${expandedMenus.users ? 'text-white' : 'text-slate-300 hover-bg-slate'}`}
                    onClick={() => toggleMenu('users')}
                  >
                    <div className="d-flex align-items-center">
                      <FaUsers className="width-20 me-3" />
                      <span className="fw-medium">Maamulka Isticmaalaha</span>
                    </div>
                    <i className={`fas fa-chevron-${expandedMenus.users ? 'up' : 'down'} small opacity-50`}></i>
                  </div>
                  {expandedMenus.users && (
                    <ul className="nav flex-column ms-3 mt-1 ps-2 border-start border-secondary border-opacity-25">
                      <li className="nav-item"><Link to="/users" className={`nav-link py-1 ps-3 small ${location.pathname === '/users' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Dhamaan Isticmaalayaasha</Link></li>
                      <li className="nav-item"><Link to="/register" className={`nav-link py-1 ps-3 small ${location.pathname === '/register' ? 'text-primary' : 'text-slate-400 hover-text-white'}`}>Diiwaangeli Isticmaale</Link></li>
                    </ul>
                  )}
                </li>
              )}

              <li className="nav-item">
                <Link to="/Account" className={`nav-link d-flex align-items-center py-2 px-3 rounded-3 ${location.pathname === '/Account' ? 'active-link' : 'text-slate-300 hover-bg-slate'}`}>
                  <FaUserCircle className="width-20 me-3" />
                  <span className="fw-medium">Ciwaankeyga</span>
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        {/* Install PWA Button */}
        <InstallPWA />

        {/* Sidebar Footer (Logout) */}
        <div className="p-3 border-top border-secondary border-opacity-25 bg-black bg-opacity-10">
          <button
            onClick={handleLogout}
            className="btn btn-danger w-100 d-flex align-items-center justify-content-center py-2"
          >
            <FaSignOutAlt className="me-2" />
            <span>Ka Bax</span>
          </button>
        </div>

      </div>

      <style>{`
        .sidebar {
          left: -250px;
          transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1045;
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
        }
        .sidebar-show {
          left: 0 !important;
        }
        .sidebar-hide {
          left: -250px !important;
        }
        
        @media (min-width: 981px) {
          .sidebar {
             left: 0 !important;
          }
        }

        .width-20 { width: 20px; text-align: center; }
        .text-slate-300 { color: #cbd5e1; }
        .text-slate-400 { color: #94a3b8; }
        .text-primary-light { color: #38bdf8; }
        .cursor-pointer { cursor: pointer; }
        
        .nav-link {
          min-height: 44px; /* Touch target size */
          -webkit-tap-highlight-color: transparent;
          user-select: none;
        }

        .active-link {
          background: #3b82f6;
          color: white !important;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .hover-bg-slate:hover {
          background: rgba(255, 255, 255, 0.08);
          color: white !important;
        }
        .hover-text-white:hover {
           color: white !important;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 20px;
        }
        
        /* Mobile specific enhancements */
        @media (max-width: 980px) {
           .sidebar {
             width: 280px !important; /* Slightly wider on mobile for better touch */
             box-shadow: 10px 0 30px rgba(0,0,0,0.3);
           }
        }

      `}</style>
    </>
  );
};

export default Sidebar;